/**
 * <Card />
 *
 * A simple white surface with a hairline border. Two tweakable axes:
 *   * `elevated`  -> applies our subtle `shadow-card`.
 *   * `interactive` -> adds hover affordance (lift + border darken) for
 *                      list items that navigate on click.
 *
 * Sub-components (`CardHeader`, `CardBody`, `CardFooter`) are plain
 * <div>s to keep the API obvious; consumers are encouraged to use
 * semantic elements (<article>, <section>, <h3>) via the `as` prop where
 * screen-reader structure matters.
 */
import * as React from "react";
import { cn } from "../../lib/cn";

type CardProps = React.HTMLAttributes<HTMLElement> & {
  /** Subtle shadow. Defaults to off. */
  elevated?: boolean;
  /** Turn the card into a clickable / hoverable surface. */
  interactive?: boolean;
  /** Tag to render. Defaults to `div`; use `article` / `section` where apt. */
  as?: "div" | "article" | "section" | "li";
};

export const Card = React.forwardRef<HTMLElement, CardProps>(
  ({ elevated, interactive, as: Tag = "div", className, children, ...rest }, ref) => {
    const classes = cn(
      "bg-white rounded-2xl border border-surface-border",
      elevated && "shadow-card",
      interactive &&
        "transition-all duration-150 cursor-pointer hover:border-ink hover:-translate-y-0.5 hover:shadow-raised",
      className,
    );
    // Polymorphic `as` -- we accept the small TS cast here rather than pull in
    // a heavy polymorphic-ref helper for a single component.
    const Component = Tag as React.ElementType;
    return (
      <Component ref={ref as React.Ref<HTMLElement>} className={classes} {...rest}>
        {children}
      </Component>
    );
  },
);
Card.displayName = "Card";

export function CardHeader({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("px-6 pt-5 pb-3 flex items-start justify-between gap-3", className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardBody({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("px-6 py-5", className)} {...rest}>
      {children}
    </div>
  );
}

export function CardFooter({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("px-6 py-4 border-t border-surface-border flex items-center gap-3", className)}
      {...rest}
    >
      {children}
    </div>
  );
}
